/**
 * Base Chart component.
 *
 * A thin wrapper around `echarts-for-react` that auto-applies the app's
 * theme (light/dark/neon) and provides sensible defaults. All AUI chart
 * components delegate to this.
 */

import { useRef, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { echarts } from '@/lib/charts/echarts-setup';
import { useChartTheme } from '@/lib/charts/useChartTheme';
import { cn } from '@/lib/cn';

export interface ChartProps {
  option: EChartsOption;
  height?: string | number;
  loading?: boolean;
  className?: string;
}

export function Chart({
  option,
  height = 400,
  loading = false,
  className,
}: ChartProps) {
  const chartRef = useRef<ReactECharts>(null);
  const { themeName } = useChartTheme();

  const mergedOption = useMemo<EChartsOption>(() => {
    // Auto-hide legend for treemap/pie when agent didn't explicitly set one,
    // since these chart types are self-labeling and auto-generated legends
    // can render with broken icon colors.
    const series = Array.isArray(option.series) ? option.series : option.series ? [option.series] : [];
    const isTreemap = series.length > 0 && series.every((s) => (s as { type?: string }).type === 'treemap');
    const agentSetLegend = option.legend !== undefined;

    // Apply anti-overlap defaults to axes: hideOverlap on all axes,
    // compact number formatting (K/M/B) on value axes.
    const applyAxisDefaults = (
      axis: EChartsOption['xAxis'] | EChartsOption['yAxis'],
    ) => {
      if (!axis) return axis;
      const axes = Array.isArray(axis) ? axis : [axis];
      return axes.map((a) => {
        const ax = a as Record<string, unknown>;
        const existing = (ax.axisLabel ?? {}) as Record<string, unknown>;
        const isValue = !ax.type || ax.type === 'value';
        if (!isValue) {
          return { ...ax, axisLabel: { hideOverlap: true, ...existing } };
        }
        const formatter =
          existing.formatter ??
          ((v: number) => {
            const abs = Math.abs(v);
            if (abs >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(abs >= 10_000_000_000 ? 0 : 1)}B`;
            if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
            if (abs >= 1_000) return `${(v / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}K`;
            return String(v);
          });
        return { ...ax, axisLabel: { hideOverlap: true, ...existing, formatter } };
      });
    };

    const grid: Record<string, unknown> = {
      containLabel: true,
      left: 12,
      right: 12,
      top: 40,
      bottom: 32,
      ...(option.grid as Record<string, unknown>),
    };

    // A yAxis `name` (default nameLocation 'end') renders ABOVE the grid and
    // containLabel does not reserve space for it — a tight grid.top from the
    // incoming option clips the name at the canvas edge. Clamp top so the
    // name (nameGap offset + one line of text) always fits.
    const yAxes = option.yAxis
      ? ((Array.isArray(option.yAxis) ? option.yAxis : [option.yAxis]) as Record<string, unknown>[])
      : [];
    const topNamed = yAxes.filter(
      (a) => a.name && (a.nameLocation === undefined || a.nameLocation === 'end'),
    );
    if (topNamed.length > 0) {
      const heightPx =
        typeof height === 'number' ? height : parseFloat(height) || 400;
      const toPx = (v: unknown): number | null => {
        if (typeof v === 'number') return v;
        if (typeof v === 'string') {
          const n = parseFloat(v);
          if (Number.isNaN(n)) return null;
          return v.trim().endsWith('%') ? (n / 100) * heightPx : n;
        }
        return null;
      };
      const maxNameGap = Math.max(
        ...topNamed.map((a) => (typeof a.nameGap === 'number' ? a.nameGap : 15)),
      );
      const needed = maxNameGap + 24;
      const topPx = toPx(grid.top);
      if (topPx !== null && topPx < needed) grid.top = needed;
    }

    return {
      animation: true,
      animationDuration: 400,
      ...option,
      xAxis: applyAxisDefaults(option.xAxis) as EChartsOption['xAxis'],
      yAxis: applyAxisDefaults(option.yAxis) as EChartsOption['yAxis'],
      ...(!agentSetLegend && isTreemap ? { legend: { show: false } } : {}),
      grid,
    };
  }, [option, height]);

  return (
    <div className={cn('w-full', className)}>
      <ReactECharts
        ref={chartRef}
        echarts={echarts}
        option={mergedOption}
        theme={themeName}
        style={{ height, width: '100%' }}
        showLoading={loading}
        notMerge={true}
        opts={{ renderer: 'canvas' }}
      />
    </div>
  );
}
